import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ChevronDown, ArrowRight, Sparkles, X, Loader2 } from "lucide-react";
import { PRICING_TIERS, WI_ADDON, useCurrency, formatPrice } from "@/pages/Landing";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NAVY = "#0A0F1E";
const NAVY_95 = "rgba(10,15,30,0.92)";
const BLUE = "#2563EB";
const INK = "#0F172A";
const SLATE = "#64748B";
const OFF_WHITE = "#F7F8FC";

const FAQ = [
  { q: "What counts as a user?", a: "Any teammate with an account in your workspace — Admin, Member, or Viewer. Bots and API keys don't count." },
  { q: "Can I change plans?", a: "Yes, anytime. Upgrades are prorated instantly; downgrades take effect at the next billing cycle." },
  { q: "Is there a free trial?", a: "Yes — every paid plan comes with a 14-day free trial. No credit card required to start." },
  { q: "What integrations are included?", a: "All plans include Slack, Gmail, Google Drive, GitHub, and HubSpot. Custom integrations are available on Enterprise." },
  { q: "How does on-premise work?", a: "Enterprise customers can run Ragnes in their own VPC (AWS, Azure, GCP, or on-metal). We ship a hardened Docker + Helm bundle." },
  { q: "Is my data safe?", a: "20 hard-block privacy filters run before any data leaves your device. We're DPDP Act 2023 ready and HIPAA compliant by architecture." },
];

/* ---------- Feature comparison table ---------- */
// Cell values: true = included ✓ · false = excluded ✗ · string = pill text
// ("Add-on" renders as a blue outlined pill, "Included" as a green filled pill,
// anything else as a neutral gray pill).
const PLAN_IDS = ["starter", "growth", "enterprise"];

const COMPARISON = [
  {
    section: "Knowledge Capture",
    rows: [
      { f: "Personal memory per employee", v: [true, true, true] },
      { f: "/alias chat routing", v: [true, true, true] },
      { f: "Knowledge base (PDF, DOCX, URL ingest)", v: [true, true, true] },
      { f: "Auto-capture hooks", v: [true, true, true] },
      { f: "Knowledge decay alerts", v: [true, true, true] },
      { f: "Max team members", v: ["Up to 10", "Up to 50", "Unlimited"] },
    ],
  },
  {
    section: "Integrations",
    rows: [
      { f: "Slack", v: [true, true, true] },
      { f: "Gmail", v: [true, true, true] },
      { f: "GitHub", v: [true, true, true] },
      { f: "Google Drive", v: [true, true, true] },
      { f: "HubSpot", v: [false, true, true] },
      { f: "Salesforce", v: [false, false, true] },
      { f: "WhatsApp Bot", v: [false, true, true] },
      { f: "Microsoft Teams", v: [false, false, true] },
    ],
  },
  {
    section: "Knowledge Handover",
    rows: [
      { f: "Handover pack (PDF + JSON export)", v: [true, true, true] },
      { f: "Departure countdown", v: [true, true, true] },
      { f: "Induction system (Day-1 brief)", v: [false, true, true] },
      { f: "Shadow mode (dual-bot overlap)", v: [false, true, true] },
      { f: "30/60/90 day milestone tracker", v: [false, true, true] },
      { f: "Buddy bot", v: [false, true, true] },
    ],
  },
  {
    section: "Bot Access & Collaboration",
    rows: [
      { f: "Query your own bot", v: [true, true, true] },
      { f: "Query teammates' bots (with grants)", v: [false, true, true] },
      { f: "Open bot access (workspace-wide)", v: [false, false, true] },
      { f: "File reference detection (📎 badges)", v: [false, true, true] },
      { f: "Bot access request & approve flow", v: [false, true, true] },
      { f: "Confidence score per response", v: [true, true, true] },
    ],
  },
  {
    section: "Analytics & Intelligence",
    rows: [
      { f: "Analytics dashboard", v: [false, true, true] },
      { f: "Query heatmap (7×24)", v: [false, true, true] },
      { f: "Knowledge graph + SPOF detection", v: [false, true, true] },
      { f: "Nudge rules & action-rate tracking", v: [false, true, true] },
    ],
  },
  {
    section: "Privacy & Compliance",
    rows: [
      { f: "20 hard-block privacy filters", v: [true, true, true] },
      { f: "Employee consent flow", v: [true, true, true] },
      { f: "Audit log", v: [false, true, true] },
      { f: "DPDP Act 2023 compliance mode", v: [false, true, true] },
      { f: "HIPAA compliant architecture", v: [false, false, true] },
      { f: "Two-admin deletion approval", v: [false, false, true] },
      { f: "CSV audit export", v: [false, true, true] },
      { f: "Data residency (India/UAE region)", v: [false, false, true] },
    ],
  },
  {
    section: "Industry Schemas",
    rows: [
      { f: "Patient records (Healthcare)", v: [false, true, true] },
      { f: "Student records (Education)", v: [false, true, true] },
      { f: "Case records (Legal/Professional)", v: [false, true, true] },
      { f: "Asset records (Manufacturing)", v: [false, true, true] },
      { f: "Department isolation", v: [false, true, true] },
      { f: "Custom entity schemas", v: [false, false, true] },
    ],
  },
  {
    section: "Add-ons",
    rows: [
      { f: "Work Intelligence (+$5/user/mo)", v: ["Add-on", "Add-on", "Included"] },
      { f: "API access", v: [false, false, true] },
      { f: "On-premise deployment", v: [false, false, true] },
    ],
  },
  {
    section: "Support",
    rows: [
      { f: "Community support", v: [true, true, true] },
      { f: "Email support", v: [true, true, true] },
      { f: "Priority support", v: [false, true, true] },
      { f: "Dedicated Customer Success Manager", v: [false, false, true] },
      { f: "SLA guarantee", v: [false, false, true] },
      { f: "Onboarding & setup support", v: [false, false, true] },
    ],
  },
];

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function CellValue({ v, testid }) {
  if (v === true) {
    return (
      <span className="inline-flex justify-center" data-testid={`${testid}-check`} aria-label="Included">
        <CheckCircle2 className="w-5 h-5" style={{ color: "#10B981" }} />
      </span>
    );
  }
  if (v === false) {
    return (
      <span className="inline-flex justify-center" data-testid={`${testid}-x`} aria-label="Not included">
        <X className="w-4 h-4" style={{ color: "#94A3B8" }} />
      </span>
    );
  }
  if (v === "Add-on") {
    return (
      <span
        className="inline-block rounded-full px-2.5 py-0.5 text-[11px] font-medium"
        style={{ color: BLUE, border: `1px solid ${BLUE}`, background: "rgba(37,99,235,0.06)" }}
        data-testid={`${testid}-addon`}
      >
        Add-on
      </span>
    );
  }
  if (v === "Included") {
    return (
      <span
        className="inline-block rounded-full px-2.5 py-0.5 text-[11px] font-medium text-white"
        style={{ background: "#10B981" }}
        data-testid={`${testid}-included`}
      >
        Included
      </span>
    );
  }
  return (
    <span
      className="inline-block rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ color: SLATE, background: "#F1F5F9", border: "1px solid #E2E8F0" }}
      data-testid={`${testid}-pill`}
    >
      {v}
    </span>
  );
}

export default function Pricing() {
  const { currency, country, source, setCurrency } = useCurrency();
  const [annual, setAnnual] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [showContact, setShowContact] = useState(false);

  const flag = currency === "INR" ? "🇮🇳" : "🌍";
  const notice = currency === "INR"
    ? `Showing prices in INR for ${country || "India"}`
    : "Showing prices in USD";

  return (
    <div className="min-h-screen" style={{ background: "#fff", color: INK }} data-testid="pricing-page">
      {/* NAV — mirrors landing's dark nav so it doesn't jarr */}
      <header className="sticky top-0 z-40" style={{ background: NAVY_95, backdropFilter: "blur(14px)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between text-white">
          <Link to="/" className="font-bold text-lg tracking-tight flex items-center gap-2">
            <Sparkles className="w-4 h-4" style={{ color: BLUE }} /> Ragnes
          </Link>
          <nav className="hidden lg:flex items-center gap-8 text-sm text-white/80">
            <Link to="/#industries" className="hover:text-white">Product</Link>
            <Link to="/#industries" className="hover:text-white">Industries</Link>
            <Link to="/pricing" className="text-white font-medium">Pricing</Link>
            <Link to="/#privacy" className="hover:text-white">Privacy</Link>
            <Link to="/#early-access" className="hover:text-white">Early Access</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-white/80 hover:text-white">Sign in</Link>
            <Link to="/#early-access" className="text-sm font-medium text-white rounded-full px-4 py-2" style={{ background: BLUE }}>
              Get early access →
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="py-20 text-center" style={{ background: NAVY }}>
        <div className="max-w-3xl mx-auto px-6 text-white">
          <h1 className="font-bold tracking-tight" style={{ fontSize: "clamp(32px,4.5vw,52px)", lineHeight: 1.1 }}>
            Simple, transparent pricing. No surprises.
          </h1>
          <p className="mt-4 text-lg text-slate-300">
            Everything you need to preserve institutional knowledge — flat-rate plans, billed monthly or annually.
          </p>
          <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm" data-testid="pricing-geo-notice">
            <span>{flag} {notice}</span>
            <span className="text-white/40">·</span>
            <button
              onClick={() => setCurrency(currency === "INR" ? "USD" : "INR")}
              className="text-white/90 hover:text-white underline underline-offset-4"
              data-testid="pricing-currency-toggle">
              Switch to {currency === "INR" ? "USD" : "INR"}
            </button>
            {source && <span className="text-white/40 text-xs">({source})</span>}
          </div>

          <div className="mt-6 inline-flex items-center gap-2 rounded-full p-1 bg-white/10" data-testid="pricing-billing-toggle">
            {[{ k: false, l: "Monthly" }, { k: true, l: "Annual · save ~17%" }].map((o) => (
              <button key={String(o.k)} onClick={() => setAnnual(o.k)}
                data-testid={`pricing-billing-${o.k ? "annual" : "monthly"}`}
                className={`rounded-full px-4 py-1.5 text-sm transition-colors ${annual === o.k ? "bg-white text-slate-900" : "text-white/80"}`}>
                {o.l}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURE COMPARISON TABLE — above the plan cards */}
      <FeatureComparison currency={currency} />

      {/* TIER CARDS */}
      <section className="pt-8 pb-16">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-5">
          {PRICING_TIERS.map((t) => {


            const monthly = t.monthly[currency];
            const annualPrice = t.annual[currency];
            const shown = annual ? annualPrice : monthly;
            const isFeatured = t.featured;
            const isEnt = t.id === "enterprise";
            return (
              <div key={t.id}
                className={`rounded-2xl p-8 border transition-all ${isFeatured ? "shadow-2xl scale-[1.02]" : ""}`}
                style={
                  isFeatured ? { background: BLUE, color: "#fff", borderColor: BLUE }
                  : isEnt ? { background: NAVY, color: "#fff", borderColor: NAVY }
                  : { background: "#fff", color: INK, borderColor: BLUE, borderWidth: 1 }
                }
                data-testid={`pricing-plan-${t.id}`}>
                {isFeatured && (
                  <span className="inline-block mb-3 text-[10px] uppercase tracking-wider font-semibold rounded-full bg-white/25 px-2.5 py-1">
                    Most popular
                  </span>
                )}
                <p className="text-lg font-semibold">{t.name}</p>
                <div className="mt-4 flex items-baseline gap-2" data-testid={`pricing-price-${t.id}`}>
                  <span className="text-5xl font-bold tracking-tight transition-all">
                    {formatPrice(shown, currency)}
                  </span>
                  {shown != null && (
                    <span className="text-sm" style={{ color: isFeatured || isEnt ? "rgba(255,255,255,0.75)" : SLATE }}>
                      /month
                    </span>
                  )}
                </div>
                {annual && annualPrice != null && monthly != null && (
                  <p className="mt-1 text-xs" style={{ color: isFeatured || isEnt ? "rgba(255,255,255,0.7)" : SLATE }}>
                    <span className="line-through mr-2">{formatPrice(monthly, currency)}</span>
                    billed {formatPrice(t.annualBilled[currency], currency)}/year
                  </p>
                )}
                <p className="mt-2 text-sm" style={{ color: isFeatured || isEnt ? "rgba(255,255,255,0.75)" : SLATE }}>
                  {t.cap}
                </p>
                <ul className="mt-6 space-y-3">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: isFeatured || isEnt ? "#fff" : "#10B981" }} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {isEnt ? (
                  <button onClick={() => setShowContact(true)}
                    className="mt-8 inline-flex items-center justify-center rounded-full w-full h-11 text-sm font-medium transition-transform hover:scale-[1.02]"
                    style={{ background: BLUE, color: "#fff" }}
                    data-testid={`pricing-cta-${t.id}`}>
                    Contact us <ArrowRight className="w-4 h-4 ml-1.5" />
                  </button>
                ) : (
                  <Link
                    to="/#early-access"
                    className="mt-8 inline-flex items-center justify-center rounded-full w-full h-11 text-sm font-medium transition-transform hover:scale-[1.02]"
                    style={isFeatured ? { background: "#fff", color: BLUE } : { background: INK, color: "#fff" }}
                    data-testid={`pricing-cta-${t.id}`}>
                    {t.cta} <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ADD-ONS */}
      <section className="pb-16" data-testid="pricing-addons">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-xs uppercase tracking-wider font-semibold mb-4 text-center" style={{ color: SLATE }}>Add-ons</p>
          <div className="rounded-2xl border p-8 bg-white" style={{ borderColor: "#E2E8F0" }} data-testid="pricing-addon-wi">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="flex-1 min-w-[260px]">
                <p className="text-lg font-semibold" style={{ color: INK }}>Work Intelligence</p>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: SLATE }}>
                  Passive app-category capture + employee self-reporting + Google Calendar correlation.
                  Transparent team time distribution without surveillance. Requires organisational agreement.
                </p>
                <ul className="mt-4 grid sm:grid-cols-2 gap-y-2 gap-x-6">
                  {[
                    "App category capture (no URLs, no screenshots)",
                    "Employee self-report daily log",
                    "Google Calendar meeting detection",
                    "Manager padding for offsite/training",
                    "Weekly team dashboard",
                    "Legal agreement gate",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm" style={{ color: INK }}>
                      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#10B981" }} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-right shrink-0">
                <p className="text-3xl font-bold" style={{ color: INK }} data-testid="pricing-addon-wi-price">
                  + {formatPrice(annual ? WI_ADDON.annual[currency] : WI_ADDON.monthly[currency], currency)}
                </p>
                <p className="text-sm" style={{ color: SLATE }}>/user/month</p>
                {annual && (
                  <p className="text-[11px] mt-1" style={{ color: SLATE }}>
                    <span className="line-through mr-1">{formatPrice(WI_ADDON.monthly[currency], currency)}</span> annual
                  </p>
                )}
                <Link to="/#early-access" className="mt-4 inline-flex items-center rounded-full px-5 h-10 text-sm font-medium text-white"
                  style={{ background: BLUE }} data-testid="pricing-addon-wi-cta">
                  Add to plan <ArrowRight className="w-4 h-4 ml-1.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20" style={{ background: OFF_WHITE }} data-testid="pricing-faq">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-center font-bold tracking-tight" style={{ fontSize: "clamp(28px,3.6vw,40px)", color: INK }}>
            Frequently asked questions
          </h2>
          <div className="mt-10 space-y-3">
            {FAQ.map((f, i) => (
              <div key={f.q} className="rounded-xl bg-white border overflow-hidden" style={{ borderColor: "#E2E8F0" }} data-testid={`pricing-faq-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
                  <span className="font-medium text-sm" style={{ color: INK }}>{f.q}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${openFaq === i ? "rotate-180" : ""}`} style={{ color: SLATE }} />
                </button>
                {openFaq === i && (
                  <p className="px-5 pb-5 text-sm leading-relaxed" style={{ color: SLATE }}>{f.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 text-center" style={{ background: NAVY }}>
        <div className="max-w-2xl mx-auto px-6 text-white">
          <h3 className="font-bold tracking-tight" style={{ fontSize: "clamp(24px,3vw,36px)" }}>
            Ready to never lose knowledge again?
          </h3>
          <Link to="/#early-access" className="mt-6 inline-flex items-center gap-2 rounded-full px-6 h-12 text-sm font-medium text-white transition-transform hover:scale-[1.03]"
            style={{ background: BLUE }}>
            Request early access <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {showContact && <ContactModal onClose={() => setShowContact(false)} />}
    </div>
  );
}

function FeatureComparison({ currency }) {
  const [tab, setTab] = useState("growth"); // mobile single-plan view

  const planMeta = PLAN_IDS.map((id) => {
    const t = PRICING_TIERS.find((x) => x.id === id);
    const monthly = t.monthly[currency];
    return {
      id,
      name: t.name,
      priceText: monthly == null ? "Custom" : `${formatPrice(monthly, currency)}/mo`,
    };
  });

  const sectionDivider = (section, colSpan, mobile = false) => (
    <tr key={`sec-${slugify(section)}`} data-testid={`pricing-compare-section-${mobile ? "mobile-" : ""}${slugify(section)}`}>
      <td
        colSpan={colSpan}
        className="text-sm uppercase tracking-wider"
        style={{ background: "#F1F5F9", color: INK, fontWeight: 600, padding: "10px 16px 10px 16px" }}
      >
        {section}
      </td>
    </tr>
  );

  return (
    <section className="pt-16 pb-4" data-testid="pricing-compare-section">
      <div className="max-w-6xl mx-auto px-6">
        <h2
          className="text-center font-bold tracking-tight"
          style={{ fontSize: "clamp(26px,3.4vw,38px)", color: INK }}
          data-testid="pricing-compare-heading"
        >
          Everything you get — compared
        </h2>
        <p className="mt-3 text-center text-sm max-w-2xl mx-auto" style={{ color: SLATE }}>
          Every plan includes privacy filters, consent flow, and handover packs. Upgrade for team collaboration and compliance.
        </p>

        {/* Desktop — full 4-column table (sticky first column on horizontal scroll) */}
        <div className="hidden md:block mt-10 overflow-x-auto rounded-2xl border" style={{ borderColor: "#E2E8F0" }} data-testid="pricing-compare-table">
          <table className="w-full border-collapse" style={{ minWidth: 760 }}>
            <thead>
              <tr>
                <th
                  className="text-left text-sm font-semibold sticky left-0 z-10"
                  style={{ background: NAVY, color: "#fff", padding: "16px" }}
                >
                  Feature
                </th>
                {planMeta.map((p) => (
                  <th
                    key={p.id}
                    className="text-center"
                    style={{
                      background: p.id === "growth" ? BLUE : NAVY,
                      color: "#fff",
                      padding: "14px 16px",
                      minWidth: 130,
                    }}
                    data-testid={`pricing-compare-header-${p.id}`}
                  >
                    <span className="block text-sm font-semibold">{p.name}</span>
                    <span className="block text-[11px] font-normal mt-0.5" style={{ color: "rgba(255,255,255,0.75)" }}>
                      {p.name} — {p.priceText}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((group) => (
                <Fragment key={group.section}>
                  {sectionDivider(group.section, 4)}
                  {group.rows.map((row, ri) => {
                    const rowSlug = slugify(row.f);
                    const rowBg = ri % 2 === 1 ? "#F8FAFC" : "#fff";
                    return (
                      <tr key={rowSlug} style={{ background: rowBg }} data-testid={`pricing-compare-row-${rowSlug}`}>
                        <td
                          className="text-sm sticky left-0 z-10"
                          style={{ color: INK, padding: "10px 16px", background: rowBg, borderTop: "1px solid #F1F5F9" }}
                        >
                          {row.f}
                        </td>
                        {PLAN_IDS.map((pid, i) => (
                          <td
                            key={pid}
                            className="text-center"
                            style={{ padding: "10px 16px", borderTop: "1px solid #F1F5F9" }}
                            data-testid={`pricing-compare-cell-${rowSlug}-${pid}`}
                          >
                            <CellValue v={row.v[i]} testid={`cmp-${rowSlug}-${pid}`} />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile — one plan at a time via tabs */}
        <div className="md:hidden mt-8" data-testid="pricing-compare-table-mobile">
          <div className="flex rounded-full p-1 mb-4" style={{ background: "#F1F5F9" }}>
            {planMeta.map((p) => (
              <button
                key={p.id}
                onClick={() => setTab(p.id)}
                className="flex-1 rounded-full px-3 py-2 text-sm font-medium transition-colors"
                style={tab === p.id ? { background: p.id === "growth" ? BLUE : INK, color: "#fff" } : { color: SLATE }}
                data-testid={`pricing-compare-tab-${p.id}`}
              >
                {p.name}
              </button>
            ))}
          </div>
          <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "#E2E8F0" }}>
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th
                    className="text-left text-sm font-semibold"
                    style={{ background: NAVY, color: "#fff", padding: "14px 16px" }}
                  >
                    Feature
                  </th>
                  <th
                    className="text-center"
                    style={{ background: tab === "growth" ? BLUE : NAVY, color: "#fff", padding: "14px 16px" }}
                    data-testid={`pricing-compare-header-mobile-${tab}`}
                  >
                    <span className="block text-sm font-semibold">{planMeta.find((p) => p.id === tab).name}</span>
                    <span className="block text-[11px] font-normal mt-0.5" style={{ color: "rgba(255,255,255,0.75)" }}>
                      {planMeta.find((p) => p.id === tab).priceText}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((group) => (
                  <Fragment key={group.section}>
                    {sectionDivider(group.section, 2, true)}
                    {group.rows.map((row, ri) => {
                      const rowSlug = slugify(row.f);
                      const rowBg = ri % 2 === 1 ? "#F8FAFC" : "#fff";
                      const pi = PLAN_IDS.indexOf(tab);
                      return (
                        <tr key={rowSlug} style={{ background: rowBg }} data-testid={`pricing-compare-row-mobile-${rowSlug}`}>
                          <td className="text-sm" style={{ color: INK, padding: "10px 16px", borderTop: "1px solid #F1F5F9" }}>
                            {row.f}
                          </td>
                          <td
                            className="text-center"
                            style={{ padding: "10px 16px", borderTop: "1px solid #F1F5F9" }}
                            data-testid={`pricing-compare-cell-mobile-${rowSlug}-${tab}`}
                          >
                            <CellValue v={row.v[pi]} testid={`cmp-m-${rowSlug}-${tab}`} />
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactModal({ onClose }) {  const [form, setForm] = useState({ name: "", email: "", company: "", size: "", industry: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const SIZES = ["100-500", "500-1000", "1000-5000", "5000+"];
  const INDS = ["Information Technology", "Healthcare", "Manufacturing & Operations",
                "Professional Services & Legal", "Banking & Financial Services",
                "Education & Universities", "Government & Public Sector", "Other"];

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/enterprise-enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Could not send. Please try again.");
      }
      setSent(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "h-11 w-full rounded-md border px-3 text-sm outline-none focus:ring-1 focus:ring-blue-500";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="pricing-contact-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-6" style={{ color: INK }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-lg">Get a custom Enterprise quote</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="pricing-contact-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        {sent ? (
          <div className="text-center py-8" data-testid="pricing-contact-success">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-3" style={{ color: "#10B981" }} />
            <p className="text-sm">Thanks! Our enterprise team will reach out within 24 hours.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="pricing-contact-form">
            <input required maxLength={120} className={inputCls} placeholder="Full name"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="pricing-contact-name" />
            <input type="email" required maxLength={254} className={inputCls} placeholder="Work email"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="pricing-contact-email" />
            <input required maxLength={200} className={inputCls} placeholder="Company"
              value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
              data-testid="pricing-contact-company" />
            <select required className={inputCls} value={form.size}
              onChange={(e) => setForm({ ...form, size: e.target.value })}
              data-testid="pricing-contact-size">
              <option value="">Company size</option>
              {SIZES.map((s) => <option key={s} value={s}>{s.replace("-", "–")} employees</option>)}
            </select>
            <select required className={inputCls} value={form.industry}
              onChange={(e) => setForm({ ...form, industry: e.target.value })}
              data-testid="pricing-contact-industry">
              <option value="">Industry</option>
              {INDS.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
            <textarea rows={3} maxLength={2000} className={`${inputCls} h-20 py-2 resize-none`}
              placeholder="Requirements or message (optional)"
              value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="pricing-contact-message" />
            <button type="submit" disabled={submitting}
              className="w-full h-11 rounded-full text-sm font-medium text-white transition-transform hover:scale-[1.01]"
              style={{ background: BLUE }} data-testid="pricing-contact-submit">
              {submitting ? <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Sending…</span>
                          : <>Send enquiry <ArrowRight className="w-4 h-4 inline ml-1" /></>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
